import React from "react";
import { useFormikContext } from "formik";
import { Button, IconButton, MenuItem, Switch, TextField, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, ArrowDownward, ArrowUpward, DeleteOutline } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import { getTokens, INK } from "../../styles/tokens";
import { FormField, FormGrid, FormSection, SwitchRow } from "./panelKit";
import {
  ESCADA_TIPOS,
  MAX_DEGRAUS,
  PCT_MAX,
  emptyDegrau,
  emptyPeriodo,
  diaMesToMmDd,
  mmDdToDiaMes,
  validateVendedora,
} from "./hospedagemVendedora";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    list: { display: "flex", flexDirection: "column", gap: 12 },
    item: {
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      padding: "10px 12px 14px",
      backgroundColor: t.paper,
    },
    itemHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      marginBottom: 8,
    },
    itemTitle: {
      fontFamily: t.font,
      fontSize: 13,
      fontWeight: 600,
      color: t.mode === "dark" ? t.text : INK[800],
    },
    itemActions: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      "& .MuiIconButton-root": { padding: 6, color: t.textSecondary },
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
    empty: {
      padding: "12px 14px",
      border: `1px dashed ${t.line}`,
      borderRadius: t.radius.sm,
      fontFamily: t.font,
      fontSize: 12.5,
      color: t.textSecondary,
    },
    anual: { display: "flex", alignItems: "center", gap: 4, minHeight: 40 },
    anualLabel: { fontFamily: t.font, fontSize: 13, color: t.textSecondary },
    feriados: { marginTop: 12 },
  };
});

const tr = (key, opts) => i18n.t(`queueIntegrationModal.hospedagem.${key}`, opts);
const errText = (key) => (key ? tr(`errors.${key}`) : undefined);

const HospedagemVendedoraFields = () => {
  const classes = useStyles();
  const { values, setFieldValue, submitCount } = useFormikContext();
  const escada = Array.isArray(values.escada) ? values.escada : [];
  const periodos = Array.isArray(values.periodosSemEscada) ? values.periodosSemEscada : [];

  // erros só depois de tentar salvar (mesma regra dos outros campos do modal)
  const errors = submitCount > 0 ? validateVendedora(values) : { escada: [], periodos: [] };

  const setEscada = (next) => setFieldValue("escada", next);
  const setDegrau = (i, patch) => setEscada(escada.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const moveDegrau = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= escada.length) return;
    const next = escada.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setEscada(next);
  };

  const setPeriodos = (next) => setFieldValue("periodosSemEscada", next);
  const setPeriodo = (i, patch) => setPeriodos(periodos.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const toggleAnual = (i, anual) => {
    const p = periodos[i];
    // converte o que já foi digitado em vez de apagar
    const conv = (v) => {
      if (anual) {
        const m = /^\d{4}-(\d{2}-\d{2})$/.exec(v || "");
        return m ? mmDdToDiaMes(m[1]) : "";
      }
      const mmdd = diaMesToMmDd(v);
      return mmdd ? `${new Date().getFullYear()}-${mmdd}` : "";
    };
    setPeriodo(i, { anual, inicio: conv(p.inicio), fim: conv(p.fim) });
  };

  const fieldProps = { variant: "outlined", size: "small", fullWidth: true };

  return (
    <>
      <FormSection
        title={tr("escadaTitle")}
        description={tr("escadaHelp")}
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            disabled={escada.length >= MAX_DEGRAUS}
            onClick={() => setEscada([...escada, emptyDegrau()])}
            data-testid="escada-add"
          >
            {tr("addDegrau")}
          </Button>
        }
      >
        {escada.length === 0 ? (
          <div className={classes.empty}>{tr("escadaEmpty")}</div>
        ) : (
          <div className={classes.list}>
            {escada.map((d, i) => {
              const e = errors.escada[i] || {};
              const usesPct = d.tipo === "DESCONTO_PCT" || d.tipo === "NOITE_EXTRA";
              return (
                <div className={classes.item} key={`degrau-${i}`} data-testid={`degrau-${i}`}>
                  <div className={classes.itemHead}>
                    <span className={classes.itemTitle}>{tr("degrauN", { n: i + 1 })}</span>
                    <span className={classes.itemActions}>
                      <Tooltip title={tr("moveUp")}>
                        <span>
                          <IconButton aria-label={tr("moveUp")} disabled={i === 0} onClick={() => moveDegrau(i, -1)}>
                            <ArrowUpward />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={tr("moveDown")}>
                        <span>
                          <IconButton
                            aria-label={tr("moveDown")}
                            disabled={i === escada.length - 1}
                            onClick={() => moveDegrau(i, 1)}
                          >
                            <ArrowDownward />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={tr("remove")}>
                        <IconButton
                          aria-label={tr("remove")}
                          onClick={() => setEscada(escada.filter((_, j) => j !== i))}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </div>
                  <FormGrid>
                    <FormField label={tr("tipo")} required error={errText(e.tipo)}>
                      <TextField
                        {...fieldProps}
                        select
                        value={ESCADA_TIPOS.includes(d.tipo) ? d.tipo : ""}
                        error={Boolean(e.tipo)}
                        SelectProps={{ displayEmpty: true }}
                        inputProps={{ "data-testid": `degrau-${i}-tipo`, "aria-label": tr("tipo") }}
                        onChange={(ev) => setDegrau(i, { tipo: ev.target.value })}
                      >
                        <MenuItem value="" disabled>
                          {tr("tipoSelect")}
                        </MenuItem>
                        {ESCADA_TIPOS.map((tipo) => (
                          <MenuItem key={tipo} value={tipo}>
                            {tr(`tipos.${tipo}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </FormField>
                    {usesPct && (
                      <FormField
                        label={d.tipo === "DESCONTO_PCT" ? tr("pctDesconto") : tr("pctNoite")}
                        required
                        help={tr("pctHelp", { max: PCT_MAX[d.tipo] })}
                        error={errText(e.pct)}
                      >
                        <TextField
                          {...fieldProps}
                          value={d.pct}
                          error={Boolean(e.pct)}
                          // sem min/max nativos: a validação do navegador barraria o envio
                          // em inglês antes da nossa (erro no campo)
                          inputProps={{ inputMode: "numeric", "data-testid": `degrau-${i}-pct` }}
                          onChange={(ev) => setDegrau(i, { pct: ev.target.value })}
                        />
                      </FormField>
                    )}
                    {d.tipo === "INCLUIR_EXTRA" && (
                      <FormField
                        label={tr("servicoExtraNome")}
                        required
                        help={tr("servicoExtraNomeHelp")}
                        error={errText(e.servicoExtraNome)}
                      >
                        <TextField
                          {...fieldProps}
                          value={d.servicoExtraNome}
                          error={Boolean(e.servicoExtraNome)}
                          inputProps={{ "data-testid": `degrau-${i}-extra` }}
                          onChange={(ev) => setDegrau(i, { servicoExtraNome: ev.target.value })}
                        />
                      </FormField>
                    )}
                    <FormField
                      full
                      label={tr("frase")}
                      required
                      help={tr("fraseHelp")}
                      error={errText(e.frase)}
                    >
                      <TextField
                        {...fieldProps}
                        multiline
                        minRows={2}
                        maxRows={6}
                        value={d.frase}
                        error={Boolean(e.frase)}
                        inputProps={{ "data-testid": `degrau-${i}-frase` }}
                        onChange={(ev) => setDegrau(i, { frase: ev.target.value })}
                      />
                    </FormField>
                  </FormGrid>
                </div>
              );
            })}
          </div>
        )}
      </FormSection>

      <FormSection
        title={tr("periodosTitle")}
        description={tr("periodosHelp")}
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setPeriodos([...periodos, emptyPeriodo()])}
            data-testid="periodo-add"
          >
            {tr("addPeriodo")}
          </Button>
        }
      >
        {periodos.length === 0 ? (
          <div className={classes.empty}>{tr("periodosEmpty")}</div>
        ) : (
          <div className={classes.list}>
            {periodos.map((p, i) => {
              const e = errors.periodos[i] || {};
              const dateProps = p.anual
                ? { placeholder: "DD/MM", inputProps: { maxLength: 5, inputMode: "numeric" } }
                : { type: "date" };
              return (
                <div className={classes.item} key={`periodo-${i}`} data-testid={`periodo-${i}`}>
                  <div className={classes.itemHead}>
                    <span className={classes.itemTitle}>{tr("periodoN", { n: i + 1 })}</span>
                    <span className={classes.itemActions}>
                      <Tooltip title={tr("remove")}>
                        <IconButton
                          aria-label={tr("remove")}
                          onClick={() => setPeriodos(periodos.filter((_, j) => j !== i))}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </div>
                  <FormGrid>
                    <FormField label={tr("rotulo")} required help={tr("rotuloHelp")} error={errText(e.rotulo)}>
                      <TextField
                        {...fieldProps}
                        value={p.rotulo}
                        error={Boolean(e.rotulo)}
                        inputProps={{ "data-testid": `periodo-${i}-rotulo` }}
                        onChange={(ev) => setPeriodo(i, { rotulo: ev.target.value })}
                      />
                    </FormField>
                    <FormField label={tr("anual")} help={p.anual ? tr("anualOnHelp") : tr("anualOffHelp")}>
                      <div className={classes.anual}>
                        <Switch
                          color="primary"
                          checked={Boolean(p.anual)}
                          onChange={(ev) => toggleAnual(i, ev.target.checked)}
                          inputProps={{ "aria-label": tr("anual"), "data-testid": `periodo-${i}-anual` }}
                        />
                        <span className={classes.anualLabel}>{p.anual ? tr("sim") : tr("nao")}</span>
                      </div>
                    </FormField>
                    <FormField label={tr("inicio")} required error={errText(e.inicio)}>
                      <TextField
                        {...fieldProps}
                        {...dateProps}
                        value={p.inicio}
                        error={Boolean(e.inicio)}
                        inputProps={{ ...(dateProps.inputProps || {}), "data-testid": `periodo-${i}-inicio` }}
                        onChange={(ev) => setPeriodo(i, { inicio: ev.target.value })}
                      />
                    </FormField>
                    <FormField label={tr("fim")} required error={errText(e.fim)}>
                      <TextField
                        {...fieldProps}
                        {...dateProps}
                        value={p.fim}
                        error={Boolean(e.fim)}
                        inputProps={{ ...(dateProps.inputProps || {}), "data-testid": `periodo-${i}-fim` }}
                        onChange={(ev) => setPeriodo(i, { fim: ev.target.value })}
                      />
                    </FormField>
                  </FormGrid>
                </div>
              );
            })}
          </div>
        )}
        <SwitchRow className={classes.feriados} label={tr("incluirFeriadosNacionais")} help={tr("incluirFeriadosHelp")}>
          <Switch
            color="primary"
            checked={values.incluirFeriadosNacionais === true}
            onChange={(ev) => setFieldValue("incluirFeriadosNacionais", ev.target.checked)}
            inputProps={{ "aria-label": tr("incluirFeriadosNacionais"), "data-testid": "feriados-switch" }}
          />
        </SwitchRow>
      </FormSection>
    </>
  );
};

export default HospedagemVendedoraFields;
